import React from 'react';
import OptimizedImage from './OptimizedImage';
import { imageAssets, type ImageAssetType } from '../../assets/images/index';

interface OptimizedImageByNameProps {
  imageName: keyof typeof imageAssets;
  alt: string;
  className?: string;
  loading?: 'eager' | 'lazy';
  sizes?: string;
  width?: number;
  height?: number;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Simplified OptimizedImage component that accepts an image name
 * and automatically uses the correct paths for all formats.
 * 
 * Usage:
 * <OptimizedImageByName imageName="giftImg" alt="Gift Image" className="w-full" />
 * 
 * This is preferred over OptimizedImage when using predefined assets.
 */
export default function OptimizedImageByName({
  imageName,
  alt,
  className,
  loading = 'lazy',
  sizes,
  width,
  height,
  onLoad,
  onError
}: OptimizedImageByNameProps) {
  const imageAsset = imageAssets[imageName];
  
  if (!imageAsset) {
    console.error(`Image asset '${imageName}' not found`);
    return null;
  }

  return (
    <OptimizedImage
      src={imageAsset.png}
      srcWebp={imageAsset.webp}
      srcAvif={imageAsset.avif}
      alt={alt}
      className={className}
      loading={loading}
      sizes={sizes}
      width={width}
      height={height}
      onLoad={onLoad}
      onError={onError}
    />
  );
}

// Export available image names for TypeScript autocomplete
export type AvailableImageNames = keyof typeof imageAssets; 